import { cn } from '../lib/cn';
import { Button } from './Button';

export interface PaginationProps {
  hasNext: boolean;
  hasPrevious: boolean;
  onNext: () => void;
  onPrevious: () => void;
  /** Rendered on the left, e.g. "Showing 25 matters". Optional. */
  summary?: string;
  loading?: boolean;
  className?: string;
}

/**
 * Cursor pagination controls.
 *
 * There are no page numbers and there is no total, and that is not an omission.
 * The API uses DRF `CursorPagination` (`kernel/api/pagination.py`) because offset
 * pagination drifts when rows are inserted mid-scroll and degrades badly on a large
 * tenant. A cursor genuinely has no page number — showing "page 4 of 12" over a
 * cursor API means inventing both figures.
 *
 * If a screen truly needs a total, that is a separate counted endpoint, and it needs
 * to be a deliberate decision because counting a scoped queryset is not free.
 */
export function Pagination({
  hasNext,
  hasPrevious,
  onNext,
  onPrevious,
  summary,
  loading,
  className,
}: PaginationProps) {
  return (
    <nav
      aria-label="Pagination"
      className={cn(
        'flex items-center justify-between gap-4 border-t px-4 py-2.5',
        className,
      )}
    >
      <p className="text-xs text-ink-muted">{summary}</p>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={onPrevious}
          disabled={!hasPrevious || loading}
        >
          Previous
        </Button>
        <Button size="sm" variant="secondary" onClick={onNext} disabled={!hasNext || loading}>
          Next
        </Button>
      </div>
    </nav>
  );
}
